import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  Card,
  Chip,
  Grid,
  Group,
  Pagination,
  Paper,
  SegmentedControl,
  Select,
  Skeleton,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconFileSpreadsheet, IconSearch, IconSearchOff, IconX } from "@tabler/icons-react";
import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { logClick, useLookups, useSearch } from "../../api/hooks";
import type { SearchMode } from "../../api/types";
import { EmptyState } from "../../components/empty-state";
import { SearchFilters } from "./filters";
import { DocumentHeader, documentLink, ResultGroup } from "./result-group";
import { SummaryPanel } from "./summary-panel";
import {
  clearedFilters,
  decideFilterSync,
  hasActiveFilters,
  parseSearchParams,
  pushHistory,
  readHistory,
  readSavedFilters,
  savedSubset,
  saveFilters,
  serializeSearchParams,
} from "./url-state";

export function SearchPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const query = useMemo(() => parseSearchParams(params), [params]);
  const [text, setText] = useState(query.q);
  const [history, setHistory] = useState<string[]>(() => readHistory());
  const result = useSearch(query.q ? query : null);
  const lookups = useLookups();
  const llmEnabled = Boolean(lookups.data?.llm);
  const exportOn = lookups.data?.modules?.export ?? true;
  const historyOn = lookups.data?.modules?.["search-log"] ?? true;

  useEffect(() => setText(query.q), [query.q]);
  useEffect(() => {
    if (query.q.trim()) setHistory(pushHistory(query.q.trim()));
  }, [query.q]);
  // Saved filters: URL changes made by this page's own controls are remembered (even a bare URL
  // means "back to the defaults" then); a bare arrival from outside (nav link, logo, first visit)
  // starts from what was saved. The rule lives in decideFilterSync; the ref marks self-made writes.
  const selfNav = useRef(false);
  useEffect(() => {
    const decision = decideFilterSync({
      bare: params.toString() === "",
      selfNav: selfNav.current,
      hasSaved: readSavedFilters() !== null,
      hasFilters: savedSubset(query) !== "",
    });
    selfNav.current = false; // consumed: the next URL change is judged on its own
    if (decision === "restore") {
      const saved = readSavedFilters();
      if (saved) setParams(serializeSearchParams({ ...query, ...saved }), { replace: true });
    } else if (decision === "save") {
      saveFilters(query);
    }
  }, [params, query, setParams]);
  const hasFilters = hasActiveFilters(query);

  const update = (next: typeof query) => {
    const target = serializeSearchParams(next);
    // Same URL (Search pressed again): no navigation happens, the effect would not run, and the
    // flag would stay set for the next outside arrival — which would then be "saved" over.
    if (target.toString() === params.toString()) return;
    selfNav.current = true;
    setParams(target);
  };
  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    const q = text.trim();
    if (!q) return;
    update({ ...query, q, page: 1 });
  };

  const data = result.data;
  const modes: { value: SearchMode; label: string }[] = [
    { value: "hybrid", label: t("search.mode.hybrid") },
    { value: "fts", label: t("search.mode.fts") },
    { value: "semantic", label: t("search.mode.semantic") },
    { value: "exact", label: t("search.mode.exact") },
  ];

  return (
    <Stack gap="md">
      <form onSubmit={submit}>
        <Stack gap="xs">
          <TextInput
            size="lg"
            value={text}
            onChange={(e) => setText(e.currentTarget.value)}
            placeholder={t("search.placeholder")}
            leftSection={<IconSearch size={20} />}
            rightSection={
              text ? (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={() => setText("")}
                  aria-label={t("common.clear")}
                >
                  <IconX size={16} />
                </ActionIcon>
              ) : null
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) submit();
            }}
            autoFocus
          />
          <Group justify="space-between" wrap="wrap" gap="xs">
            <Group gap="sm" wrap="wrap">
              <SegmentedControl
                size="xs"
                data={modes}
                value={query.mode}
                onChange={(v) => update({ ...query, mode: v as SearchMode, page: 1 })}
              />
              <Select
                size="xs"
                w={220}
                data={(["relevance", "date_desc", "date_asc"] as const).map((v) => ({
                  value: v,
                  label: t(`search.sort.${v}`),
                }))}
                value={query.sort}
                onChange={(v) => v && update({ ...query, sort: v as typeof query.sort, page: 1 })}
                allowDeselect={false}
                aria-label={t("search.sort.label")}
              />
              <Tooltip label={t("search.deepHint")}>
                <Switch
                  size="sm"
                  label={t("search.deep")}
                  checked={query.depth === "deep"}
                  onChange={(e) =>
                    update({
                      ...query,
                      depth: e.currentTarget.checked ? "deep" : "normal",
                      page: 1,
                    })
                  }
                />
              </Tooltip>
            </Group>
            <Button
              type="submit"
              loading={result.isFetching}
              leftSection={<IconSearch size={16} />}
            >
              {t("search.button")}
            </Button>
          </Group>
          {historyOn && !query.q && history.length > 0 && (
            <Group gap={6}>
              <Text size="xs" c="dimmed">
                {t("search.history")}
              </Text>
              {history.map((h) => (
                <Chip
                  key={h}
                  size="xs"
                  checked={false}
                  onClick={() => {
                    setText(h);
                    update({ ...query, q: h, page: 1 });
                  }}
                >
                  {h}
                </Chip>
              ))}
            </Group>
          )}
        </Stack>
      </form>

      <Grid gap="md">
        <Grid.Col span={{ base: 12, md: 3 }}>
          <Paper withBorder p="md">
            <SearchFilters value={query} facets={data?.facets} onChange={update} />
          </Paper>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 9 }}>
          <Stack gap="md">
            {result.isError && (
              <Alert color="red" title={t("common.error")}>
                {(result.error as Error).message}
              </Alert>
            )}
            {result.isLoading && (
              <Stack>
                <Skeleton h={90} />
                <Skeleton h={90} />
                <Skeleton h={90} />
              </Stack>
            )}
            {!query.q && !result.isLoading && <Text c="dimmed">{t("search.intro")}</Text>}
            {data && (
              <>
                <Group justify="space-between" wrap="wrap">
                  <Title order={4}>
                    {data.docsTotal === 0 ? "" : t("search.found", { count: data.docsTotal })}
                  </Title>
                  <Group gap="sm">
                    {exportOn && data.docsTotal > 0 && (
                      <Tooltip label={t("search.exportHint")}>
                        <Button
                          component="a"
                          href={`/api/search/export?${serializeSearchParams({ ...query, page: 1 }).toString()}`}
                          variant="default"
                          size="compact-sm"
                          leftSection={<IconFileSpreadsheet size={14} />}
                        >
                          {t("search.export")}
                        </Button>
                      </Tooltip>
                    )}
                    <Text size="xs" c="dimmed">
                      {t("search.took", { ms: data.timings.totalMs })}
                    </Text>
                  </Group>
                </Group>
                {llmEnabled && data.docsTotal > 0 && (
                  <SummaryPanel
                    key={serializeSearchParams({ ...query, page: 1 }).toString()}
                    query={query}
                  />
                )}
                {data.documentsByNumber.length > 0 && (
                  <Card withBorder padding="sm" bg="var(--mantine-color-default-hover)">
                    <Stack gap={6}>
                      <Text size="sm" fw={600}>
                        {data.query.regNumber
                          ? t("search.byNumber", { n: data.query.regNumber })
                          : t("search.byTitle")}
                      </Text>
                      {data.documentsByNumber.map((d) => (
                        <Group key={d.id} gap="sm" wrap="wrap">
                          <Anchor
                            component={Link}
                            to={documentLink(d, query.q, undefined, query.mode)}
                            fw={500}
                            size="sm"
                            onClick={() => logClick(data.searchId, d.id)}
                          >
                            {d.title}
                          </Anchor>
                          <DocumentHeader doc={d} />
                        </Group>
                      ))}
                    </Stack>
                  </Card>
                )}
                {data.groups.map((g, i) => (
                  <ResultGroup
                    key={g.document.id}
                    group={g}
                    rank={(data.page - 1) * data.pageSize + i + 1}
                    searchId={data.searchId}
                    q={query.q}
                    mode={query.mode}
                  />
                ))}
                {data.docsTotal > data.pageSize && (
                  <Pagination
                    total={Math.ceil(data.docsTotal / data.pageSize)}
                    value={data.page}
                    onChange={(p) => update({ ...query, page: p })}
                  />
                )}
                {data.docsTotal === 0 && query.q && (
                  <EmptyState
                    icon={<IconSearchOff size={22} />}
                    title={t("search.noneTitle", { q: query.q })}
                    hint={hasFilters ? t("search.noneHintFilters") : t("search.noneHint")}
                    action={
                      hasFilters ? (
                        <Button
                          variant="light"
                          size="xs"
                          onClick={() => update(clearedFilters(query))}
                        >
                          {t("search.filters.clear")}
                        </Button>
                      ) : query.depth !== "deep" ? (
                        <Button
                          variant="light"
                          size="xs"
                          onClick={() => update({ ...query, depth: "deep", page: 1 })}
                        >
                          {t("search.deep")}
                        </Button>
                      ) : undefined
                    }
                  />
                )}
              </>
            )}
          </Stack>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
